import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Inject,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from "@angular/material/legacy-dialog";
import { AccountAppValue, RecoveryQuestion } from "@fabric-types/app-account";
import { Store } from "@ngrx/store";
import { DialogLayoutComponent, NavbarOptions } from "@obg/mat.ui.core";
import { CommonState, isEmailValid } from "@obg/ngx.common";
import { Jurisdiction, JurisdictionSelector } from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { TelemetryService, DocumentRef } from "@obg/ngx.global.evo";
import { GtmEventAction } from "@obg/ngx.gtm/adaptive";
import { OBG_VALIDATORS } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, merge, Observable, Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";

import { LoginAction, LoginSelector, ResetPasswordModel } from "../sdk";

import { RenewPasswordContainerData } from "./renew-password.model";

const USERNAME_CONTROL_NAME = "username";

@Component({
	selector: "obg-m-renew-password",
	styleUrls: ["./renew-password.container.scss"],
	templateUrl: "./renew-password.container.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class RenewPasswordContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-renew-password") hostCssClass = true;
	@HostBinding("attr.test-id") testId = "obg-m-renew-password-modal";
	@ViewChild("dialog") dialogLayout: DialogLayoutComponent | undefined;

	@Output() success = new EventEmitter();

	formGroup: UntypedFormGroup;
	jurisdiction: Jurisdiction | undefined;
	isBusy = false;
	isFormSubmitted = false;
	isDesktop = this.deviceConfigService.isDesktop();
	navbarOptions: NavbarOptions = {
		hideBackButton: false,
		showCloseButton: this.isDesktop,
		preventDefaultBack: true,
	};

	hasSucceeded$: Observable<boolean> | undefined;
	hasFailed$: Observable<boolean> | undefined;
	isAamsJurisdiction$: Observable<Jurisdiction | undefined> | undefined;
	recoveryQuestions$: Observable<RecoveryQuestion[]> | undefined;

	private data$$: Subscription | undefined;
	private jurisdiction$$: Subscription | undefined;

	constructor(
		private store: Store<CommonState>,
		private formBuilder: UntypedFormBuilder,
		private loginAction: LoginAction,
		private telemetryService: TelemetryService,
		private deviceConfigService: DeviceConfigService,
		@Inject(MAT_DIALOG_DATA) public data: RenewPasswordContainerData | undefined,
		private changeDetector: ChangeDetectorRef,
		private documentRef: DocumentRef
	) {
		this.formGroup = this.formBuilder.group({
			[USERNAME_CONTROL_NAME]: this.formBuilder.control("", [Validators.required, OBG_VALIDATORS.email]),
		});
	}

	ngOnInit() {
		if (!!this.data?.selectedUserEmail && isEmailValid(this.data?.selectedUserEmail)) {
			const usernameControl = this.formGroup.get(USERNAME_CONTROL_NAME);
			usernameControl?.setValue(this.data.selectedUserEmail);
			usernameControl?.disable();
			usernameControl?.markAsTouched();
		}

		this.isAamsJurisdiction$ = this.isJurisdiction(Jurisdiction.Aams);
		this.subscribeToData();
	}

	private subscribeToData() {
		this.hasSucceeded$ = combineLatest([
			this.store.select(LoginSelector.isForgotPasswordByEmailSuccess),
			this.store.select(LoginSelector.isForgotPasswordByEmailAndSecurityQuestionSuccess),
		]).pipe(
			map(([emailSuccess, emailAndSecurityQuestionSuccess]) => emailSuccess || emailAndSecurityQuestionSuccess),
			filter((isSuccess) => isSuccess),
			tap(() => {
				this.isBusy = false;
				this.telemetryService.track({
					event: GtmEventAction.ForgottenPasswordSuccess,
				});
				this.success.emit();
			})
		);

		this.hasFailed$ = combineLatest([
			this.store.select(LoginSelector.isForgotPasswordByEmailFailed),
			this.store.select(LoginSelector.isForgotPasswordByEmailAndSecurityQuestionFailed),
		]).pipe(
			map(([emailFailed, emailAndSecurityQuestionFailed]) => emailFailed || emailAndSecurityQuestionFailed),
			filter((hasFailed) => hasFailed),
			tap(() => {
				this.isBusy = false;
			})
		);

		this.recoveryQuestions$ = new Observable<RecoveryQuestion[]>(
			this.documentRef.obgEventBus.retrieve(AccountAppValue.GetRecoveryQuestions)
		);

		this.jurisdiction$$ = this.store
			.select(JurisdictionSelector.get)
			.pipe(
				ignoreNil(),
				tap((jurisdiction) => (this.jurisdiction = jurisdiction)),
				filter((jurisdiction) => jurisdiction === Jurisdiction.Aams),
				tap(() => this.addControlsForAAMS())
			)
			.subscribe();

		this.data$$ = merge(this.hasSucceeded$, this.hasFailed$, this.recoveryQuestions$).subscribe(() =>
			this.changeDetector.markForCheck()
		);
	}

	private addControlsForAAMS() {
		this.formGroup.addControl("recoveryQuestion", this.formBuilder.control("", [Validators.required]));
		this.formGroup.addControl("recoveryAnswer", this.formBuilder.control("", [Validators.required]));
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.jurisdiction$$?.unsubscribe();
	}

	submit() {
		const data: ResetPasswordModel = this.formGroup.getRawValue();

		if (this.formGroup.invalid) {
			return;
		}

		this.telemetryService.track({
			event: GtmEventAction.LoginPasswordFormSubmit,
		});

		this.isBusy = true;
		this.isFormSubmitted = true;

		if (this.jurisdiction === Jurisdiction.Aams) {
			this.store.dispatch(
				this.loginAction.resetPasswordByEmailAndSecurityQuestion({
					email: data.username,
					securityQuestion: data.recoveryQuestion ?? "",
					answer: data.recoveryAnswer ?? "",
				})
			);
			return;
		}

		this.store.dispatch(this.loginAction.resetPasswordByEmail(data.username));
	}

	isJurisdiction(jurisdiction: Jurisdiction) {
		return this.store
			.select(JurisdictionSelector.get)
			.pipe(filter((currentJurisdiction) => currentJurisdiction === jurisdiction));
	}
}
